// src/hooks/useJugImageLogic.jsx
import { useMemo } from 'react';

export const useJugImageLogic = () => {
  
  // Función para calcular días transcurridos desde la entrega
  const calculateDaysFromDelivery = (deliveryDate) => {
    if (!deliveryDate) return 0;
    
    const delivery = new Date(deliveryDate);
    const now = new Date();
    const diffTime = Math.abs(now - delivery);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Función principal para obtener la imagen del botellón
  const getJugImage = (jug) => {
    if (!jug) return '/assets/img/jugs-states/nobg-new-100.png'; // Imagen por defecto

    const status = jug.order_detail_status;
    const deliveryDate = jug.actual_delivery_date || jug.scheduled_delivery_date;
    
    switch (status) {
      case 'recently_ordered':
        return '/assets/img/jugs-states/nobg-new-100.png';
      
      case 'ready_for_production':
      case 'in_washing':
      case 'in_filling':
        return '/assets/img/jugs-states/nobg-refill-0.png';
      
      case 'ready_for_delivery':
      case 'in_transit':
        return '/assets/img/jugs-states/nobg-new-100.png';
      
      case 'delivered':
        const daysElapsed = calculateDaysFromDelivery(deliveryDate);
        
        if (daysElapsed < 1) {
          return '/assets/img/jugs-states/nobg-new-100.png';
        } else if (daysElapsed === 1) {
          return '/assets/img/jugs-states/nobg-new-100.png';
        } else if (daysElapsed === 2) {
          return '/assets/img/jugs-states/nobg-refill-90.png';
        } else if (daysElapsed >= 3 && daysElapsed < 8) {
          return '/assets/img/jugs-states/nobg-refill-80.png';
        } else if (daysElapsed >= 8 && daysElapsed < 12) {
          return '/assets/img/jugs-states/nobg-refill-70.png';
        } else if (daysElapsed >= 12 && daysElapsed < 16) {
          return '/assets/img/jugs-states/nobg-refill-60.png';
        } else if (daysElapsed >= 16 && daysElapsed < 20) {
          return '/assets/img/jugs-states/nobg-refill-50.png';
        } else if (daysElapsed >= 20 && daysElapsed < 24) {
          return '/assets/img/jugs-states/nobg-refill-40.png';
        } else if (daysElapsed >= 24 && daysElapsed < 28) {
          return '/assets/img/jugs-states/nobg-refill-30.png';
        } else if (daysElapsed >= 28 && daysElapsed < 33) {
          return '/assets/img/jugs-states/nobg-refill-20.png';
        } else {
          return '/assets/img/jugs-states/nobg-refill-0.png';
        }
      
      case 'to_be_refilled':
      case 'to_be_returned':
      case 'overdue_for_return':
        return '/assets/img/jugs-states/nobg-refill-0.png';
      
      default:
        return '/assets/img/jugs-states/nobg-new-100.png';
    }
  };

  // Función para obtener el porcentaje de llenado numérico
  const getJugFillPercentage = (jug) => {
    if (!jug) return 100;

    const status = jug.order_detail_status;
    const deliveryDate = jug.actual_delivery_date || jug.scheduled_delivery_date;
    
    switch (status) {
      case 'recently_ordered':
      case 'ready_for_delivery':
      case 'in_transit':
        return 100;
      
      case 'ready_for_production':
      case 'in_washing':
      case 'in_filling':
      case 'to_be_refilled':
      case 'to_be_returned':
      case 'overdue_for_return':
        return 0;
      
      case 'delivered':
        const daysElapsed = calculateDaysFromDelivery(deliveryDate);
        
        if (daysElapsed < 1) {
          return 100;
        } else if (daysElapsed === 1) {
          return 100;
        } else if (daysElapsed >= 2 && daysElapsed < 4) {
          return 90;
        } else if (daysElapsed >= 4 && daysElapsed < 8) {
          return 80;
        } else if (daysElapsed >= 8 && daysElapsed < 12) {
          return 70;
        } else if (daysElapsed >= 12 && daysElapsed < 16) {
          return 60;
        } else if (daysElapsed >= 16 && daysElapsed < 20) {
          return 50;
        } else if (daysElapsed >= 20 && daysElapsed < 24) {
          return 40;
        } else if (daysElapsed >= 24 && daysElapsed < 28) {
          return 30;
        } else if (daysElapsed >= 28 && daysElapsed < 33) {
          return 20;
        } else {
          return 0;
        }
      
      default:
        return 100;
    }
  };

  // Función para obtener información completa sobre el estado del botellón
  const getJugStateInfo = (jug) => {
    if (!jug) return { 
      daysElapsed: 0, 
      fillPercentage: 100, 
      isNewBottle: true,
      imagePath: '/assets/img/jugs-states/nobg-new-100.png',
      status: 'unknown'
    };

    const deliveryDate = jug.actual_delivery_date || jug.scheduled_delivery_date;
    const daysElapsed = calculateDaysFromDelivery(deliveryDate);
    const fillPercentage = getJugFillPercentage(jug);
    const status = jug.order_detail_status;
    const imagePath = getJugImage(jug);
    
    // Determinar si es botellón nuevo (imagen nobg-new-100.png)
    const isNewBottle = imagePath.includes('nobg-new-100.png');

    return {
      daysElapsed,
      fillPercentage,
      isNewBottle,
      deliveryDate,
      imagePath,
      status
    };
  };

  return {
    getJugImage,
    getJugFillPercentage,
    getJugStateInfo,
    calculateDaysFromDelivery
  };
};